import { useEffect, useMemo, useState } from "react";
import { api, getApiErrorMessage } from "../services/api";
import { createTranslator } from "../i18n";

export default function Login({
  setToken,
  language,
  setLanguage,
  initialEmail = "",
  authMessage = "",
  clearAuthMessage,
}) {
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [error, setError] = useState("");
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const [verifyMessage, setVerifyMessage] = useState("");
  const t = useMemo(() => createTranslator(language), [language]);

  useEffect(() => {
    setEmail(initialEmail || "");
  }, [initialEmail]);

  const login = async () => {
    setLoading(true);
    setError("");
    setNeedsVerification(false);
    setResendMessage("");
    setVerifyMessage("");

    try {
      const res = await api.post("/login", {
        email,
        password,
      });

      if (res.data?.user?.preferred_language) {
        setLanguage?.(res.data.user.preferred_language);
      }
      localStorage.setItem("token", res.data.token);
      setToken(res.data.token);
    } catch (err) {
      console.error(err);
      const nextMessage = getApiErrorMessage(err, t("auth.login.error", "Could not sign in."));
      setError(nextMessage);
      if (err?.response?.status === 403) {
        setNeedsVerification(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async () => {
    if (!email.trim()) {
      setError(t("auth.login.email_required", "Email is required."));
      return;
    }
    setResendLoading(true);
    setError("");
    setResendMessage("");
    setVerifyMessage("");
    try {
      const response = await api.post("/auth/resend-verification", {
        email,
      });
      setNeedsVerification(true);
      setResendMessage(response.data?.message || "");
      clearAuthMessage?.();
    } catch (err) {
      setError(getApiErrorMessage(err, t("auth.verification.resend_error", "Could not resend the verification code.")));
    } finally {
      setResendLoading(false);
    }
  };

  const verifyEmailCode = async () => {
    if (!email.trim()) {
      setError(t("auth.login.email_required", "Email is required."));
      return;
    }
    if (!verificationCode.trim()) {
      setError(t("auth.verification.code_required", "Verification code is required."));
      return;
    }

    setVerifyLoading(true);
    setError("");
    setVerifyMessage("");
    try {
      const response = await api.post("/auth/verify-email", {
        email,
        code: verificationCode,
      });
      setNeedsVerification(false);
      setVerifyMessage(
        response.data?.message ||
          t("auth.verification.success", "Email verified. You can sign in now.")
      );
      setVerificationCode("");
      clearAuthMessage?.();
    } catch (err) {
      setError(getApiErrorMessage(err, t("auth.verification.confirm_error", "Could not verify this code.")));
    } finally {
      setVerifyLoading(false);
    }
  };

  return (
    <div className="auth-form">
      <h2>{t("auth.login.title", "Sign in")}</h2>
      <p>{t("auth.login.description", "Use your account to open the map and compare markets automatically.")}</p>

      <input
        className="auth-input"
        placeholder={t("auth.email", "Email")}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <input
        className="auth-input"
        placeholder={t("auth.password", "Password")}
        value={password}
        type="password"
        onChange={(e) => setPassword(e.target.value)}
      />

      {authMessage && (
        <div className="auth-success">
          <strong style={{ display: "block", marginBottom: 6 }}>
            {t("auth.verification.title", "Check your email")}
          </strong>
          <div>{authMessage}</div>
        </div>
      )}

      {error && <div className="auth-error">{error}</div>}
      {resendMessage && <div className="auth-success">{resendMessage}</div>}
      {verifyMessage && <div className="auth-success">{verifyMessage}</div>}

      {(needsVerification || authMessage) && (
        <div className="auth-help-block">
          <input
            className="auth-input"
            placeholder={t("auth.verification.code_placeholder", "Verification code")}
            value={verificationCode}
            onChange={(event) => setVerificationCode(event.target.value)}
          />
          <button className="auth-primary" onClick={verifyEmailCode} disabled={verifyLoading}>
            {verifyLoading
              ? t("auth.verification.confirming", "Confirming...")
              : t("auth.verification.confirm", "Confirm code")}
          </button>
          <button className="auth-secondary" onClick={resendVerification} disabled={resendLoading}>
            {resendLoading
              ? t("auth.verification.resending", "Resending...")
              : t("auth.verification.resend", "Resend verification")}
          </button>
        </div>
      )}

      <button className="auth-primary" onClick={login} disabled={loading}>
        {loading ? t("auth.login.loading", "Signing in...") : t("auth.login.button", "Sign in")}
      </button>
    </div>
  );
}
